import { useSyncExternalStore } from "react";
import { lerLocal, gravarLocal } from "@/lib/armazenamento-local";

export type TutelaeTheme = "dark" | "light";
/** TUT-153 — 'system' segue o prefers-color-scheme do dispositivo. */
export type ThemePreference = TutelaeTheme | "system";

const STORAGE_KEY = "tutelae-theme";

/**
 * Uma preferência única de tema para o produto e para o Console Brevi.
 *
 * O casco do tenant e o Console são árvores de autenticação separadas, mas a
 * escolha pertence à mesma pessoa no mesmo navegador. Duplicar o estado faria
 * a tela piscar de volta ao escuro ao atravessar de uma superfície para outra.
 *
 * TUT-153 promoveu o estado a uma mini-store de módulo: o Perfil e o casco
 * renderizam o MESMO valor no mesmo instante (dois useState divergiriam até o
 * próximo remount), e a preferência ganhou o terceiro valor 'system'. O
 * localStorage vira cache de primeira pintura; a verdade durável mora no
 * perfil do usuário e chega com a sessão via adoptThemePreference().
 */

function readStoredPreference(): ThemePreference {
  try {
    const stored = lerLocal(STORAGE_KEY);
    return stored === "light" || stored === "system" ? stored : "dark";
  } catch {
    return "dark";
  }
}

function resolve(preference: ThemePreference): TutelaeTheme {
  if (preference !== "system") return preference;
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  } catch {
    return "dark";
  }
}

let preference: ThemePreference = readStoredPreference();
let theme: TutelaeTheme = resolve(preference);
let applied = false;
const listeners = new Set<() => void>();

function applyTheme() {
  document.documentElement.dataset.theme = theme;
  applied = true;
}

function emit() {
  for (const listener of listeners) listener();
}

// Com 'system', o tema muda sob os pés quando o sistema operacional troca —
// o listener re-resolve sem exigir clique nem recarga.
try {
  window
    .matchMedia("(prefers-color-scheme: light)")
    .addEventListener("change", () => {
      if (preference !== "system") return;
      const next = resolve("system");
      if (next === theme) return;
      theme = next;
      applyTheme();
      emit();
    });
} catch {
  // Ambientes sem matchMedia (testes antigos) seguem só com dark/light.
}

/**
 * Adota uma preferência sem disparar persistência no servidor — o caminho da
 * SESSÃO (o valor durável chegando) e de qualquer superfície que já tenha
 * gravado por conta própria.
 */
export function adoptThemePreference(next: ThemePreference) {
  if (next === preference && applied) return;
  preference = next;
  theme = resolve(next);
  try {
    gravarLocal(STORAGE_KEY, next);
  } catch {
    // O tema continua funcional quando o navegador bloqueia armazenamento.
  }
  applyTheme();
  emit();
}

function subscribe(listener: () => void) {
  if (!applied) applyTheme();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// O snapshot precisa ser um valor estável por render — string codificando o
// par (preferência, tema resolvido) evita criar objeto novo a cada leitura.
function snapshot() {
  return `${preference}|${theme}`;
}

export function useThemePreference() {
  const encoded = useSyncExternalStore(subscribe, snapshot);
  const [pref, resolved] = encoded.split("|") as [ThemePreference, TutelaeTheme];

  const setPreference = (next: ThemePreference) => adoptThemePreference(next);

  // O botão do casco alterna entre claro/escuro EXPLÍCITOS a partir do que se
  // vê — quem estava em 'system' sai dele ao tocar o botão, que é o gesto de
  // "quero esta cor, e ponto". TUT-546: devolve a cor aplicada, e é ELA que o
  // casco grava no perfil (o escritório e o portal), sem recalcular.
  const toggleTheme = (): TutelaeTheme => {
    const proxima: TutelaeTheme = resolved === "dark" ? "light" : "dark";
    adoptThemePreference(proxima);
    return proxima;
  };

  return { theme: resolved, preference: pref, setPreference, toggleTheme };
}

/** Só para testes: volta a store ao estado de primeira carga. */
export function resetThemePreferenceForTests() {
  preference = readStoredPreference();
  theme = resolve(preference);
  applied = false;
}
